// Category configuration system
// This file manages switching between different category sets

// Import default categories
import {activitiesDefault} from './categories-default/taxonomy-activity-default';
import {feelingDefault} from './categories-default/taxonomy-feeling-default';
import {placesDefault} from './categories-default/taxonomy-place-default';
import {withWhomDefault} from './categories-default/taxonomy-withwhom-default';

// Import HVB categories
import {activitiesUnga} from './categories-hvb/taxonomy-hvb-boende';
import {feelingDefault as feelingHvb} from './categories-hvb/taxonomy-feeling-default';
import {placesBoende} from './categories-hvb/taxonomy-place-boende';
import {withWhomBoende} from './categories-hvb/taxonomy-withwhom-boende';

export type CategorySetType = 'default' | 'hvb';

interface CategorySet {
  activities: any[];
  feelings: string[];
  places: string[];
  withWhom: string[];
}

// Category grouping for HVB - groups related categories together
// Maps taxonomy ranges to group names for precise grouping
export const hvbTaxonomyGrouping = {
  'Basala behov': [
    {min: 10, max: 99}, // Egenomsorg (10-99)
    {min: 300, max: 399}, // Måltider (300-399)
  ],
  'Hem & Hushåll': [
    {min: 100, max: 199}, // Omvårdnad (100-199)
    {min: 200, max: 299}, // Hushållsarbete (200-299)
    {min: 1100, max: 1199}, // Extra Omvårdnad range (1100-1199)
  ],
  'Rörlighet': [
    {min: 400, max: 499}, // Förflyttningar (400-499)
  ],
  'Aktiviteter & Fritid': [
    {min: 500, max: 699}, // Boendeaktiviteter (500-699)
    {min: 900, max: 999}, // Avkoppling (900-999)
    {min: 1300, max: 1399}, // Extra Avkoppling range (1300-1399)
  ],
  'Kommunikation & Admin': [
    {min: 700, max: 899}, // Möten & Samtal (700-899)
    {min: 1000, max: 1099}, // Administration (1000-1099)
  ],
};

// Get group name by taxonomy number
export function getHvbGroupByTaxonomy(taxonomy: number): string {
  for (const [groupName, ranges] of Object.entries(hvbTaxonomyGrouping)) {
    for (const range of ranges) {
      if (taxonomy >= range.min && taxonomy <= range.max) {
        return groupName;
      }
    }
  }
  return 'Annat'; // Default fallback
}

export const hvbCategoryGroups = {
  'Basala behov': ['Egenomsorg', 'egenomsorg', 'Måltider', 'måltider'],
  'Hem & Hushåll': ['Hushållsarbete', 'hushållsarbete', 'Omvårdnad', 'omvardnad', 'omvårdnad'],
  'Aktiviteter & Fritid': ['Boendeaktiviteter', 'boendeaktiviteter', 'Avkoppling', 'avkoppling'],
  'Kommunikation & Admin': ['Möten', 'möten', 'Samtal', 'samtal', 'Administration', 'administration'],
  'Rörlighet': ['Förflyttningar', 'förflyttningar'],
};

// Feeling grouping for HVB
export const hvbFeelingGroups = {
  'Positiva': [
    'Glad', 'glad',
    'Pigg', 'pigg',
    'Trygg', 'trygg',
    'Lugn', 'lugn',
  ],
  'Negativa': [
    'Orolig', 'orolig',
    'Trött', 'trött',
    'Omotiverad', 'omotiverad',
    'Stressad', 'stressad',
    'Ledsen', 'ledsen',
    'Rädd', 'rädd',
    'Arg', 'arg',
  ],
  'Neutral': [
    'Neutral (t ex vid sömn)', 'neutral (t ex vid sömn)',
    'Neutral', 'neutral',
    'Annat', 'annat',
  ],
};

// Place grouping for HVB - case insensitive matching
export const hvbPlaceGroups = {
  'Inomhus (HVB)': [
    'Inomhus - Kök', 'inomhus - kök',
    'Inomhus - Vardagsrum', 'inomhus - vardagsrum',
    'Inomhus - Samtalsrum', 'inomhus - samtalsrum',
    'Inomhus - Ungdomens rum', 'inomhus - ungdomens rum',
    'Inomhus - Kontor', 'inomhus - kontor',
    'Inomhus - Aktivitetsrum', 'inomhus - aktivitetsrum',
  ],
  'Inomhus (Externt)': [
    'Inomhus - Skola', 'inomhus - skola',
    'Inomhus - Idrottsplats (gym, badhus)',
    'inomhus - idrottsplats (gym, badhus)',
    'Inomhus - Affär/frisör', 'inomhus - affär/frisör',
    'Inomhus - Myndighet', 'inomhus - myndighet',
    'Inomhus - Ungdomens hembostad', 'inomhus - ungdomens hembostad',
    'Inomhus - Hemma', 'inomhus - hemma',
    'Inomhus - Plats för nöjen (t ex bio, kafé)',
    'inomhus - plats för nöjen (t ex bio, kafé)',
    'Inomhus - Annat', 'inomhus - annat',
  ],
  'Utomhus': [
    'Utomhus - Trädgård', 'utomhus - trädgård',
    'Utomhus - Idrottsplats', 'utomhus - idrottsplats',
    'Utomhus - Natur', 'utomhus - natur',
    'Utomhus - Bad', 'utomhus - bad',
    'Utomhus - Stad/samhälle', 'utomhus - stad/samhälle',
    'Utomhus - Bil eller annat transportmedel', 'utomhus - bil eller annat transportmedel',
    'Utomhus - Annat', 'utomhus - annat',
  ],
};

// WithWhom grouping for HVB
export const hvbWithWhomGroups = {
  'På HVB': [
    'Själv', 'själv',
    'Andra ungdom/ar på HVB', 'andra ungdom/ar på hvb',
    'Personal', 'personal',
    'Personal och ungdomar', 'personal och ungdomar',
  ],
  'Externt': [
    'Kompis/ar utanför HVB', 'kompis/ar utanför hvb',
    'Vårdnadshavare', 'vårdnadshavare',
    'Skolpersonal', 'skolpersonal',
  ],
  'Myndigheter': [
    'God man', 'god man',
    'Socialtjänst', 'socialtjänst',
    'Hälso- och sjukvård', 'hälso- och sjukvård',
    'Psykiatri', 'psykiatri',
    'Rättsvårdande', 'rättsvårdande',
    'Flera aktörer', 'flera aktörer',
    'Annan myndighet', 'annan myndighet',
    'Annan', 'annan',
  ],
};

// Get grouped display name for HVB categories (uses taxonomy number if available)
export function getHvbGroupForCategory(categoryOrTaxonomy: string | number, taxonomy?: number): string {
  // If taxonomy is provided, use it
  if (taxonomy !== undefined && taxonomy !== null && !isNaN(taxonomy)) {
    return getHvbGroupByTaxonomy(taxonomy);
  }

  // If first param is a number, use taxonomy-based grouping
  if (typeof categoryOrTaxonomy === 'number') {
    return getHvbGroupByTaxonomy(categoryOrTaxonomy);
  }

  // Try to parse as number
  const parsedTaxonomy = parseInt(categoryOrTaxonomy as string);
  if (!isNaN(parsedTaxonomy)) {
    return getHvbGroupByTaxonomy(parsedTaxonomy);
  }

  // Fallback: use category name (case insensitive)
  const lowerCategory = (categoryOrTaxonomy as string).toLowerCase();
  for (const [groupName, categories] of Object.entries(hvbCategoryGroups)) {
    if (categories.some((cat) => cat.toLowerCase() === lowerCategory)) {
      return groupName;
    }
  }
  return categoryOrTaxonomy as string;
}

// Get grouped display name for HVB feelings (case insensitive)
export function getHvbGroupForFeeling(feeling: string): string {
  if (!feeling) return 'Okänt';

  const lowerFeeling = feeling.toLowerCase();
  for (const [groupName, feelings] of Object.entries(hvbFeelingGroups)) {
    if (feelings.some((f) => f.toLowerCase() === lowerFeeling)) {
      return groupName;
    }
  }
  return feeling;
}

// Get grouped display name for HVB places (case insensitive)
export function getHvbGroupForPlace(place: string): string {
  if (!place) return 'Okänt';

  const lowerPlace = place.toLowerCase();
  for (const [groupName, places] of Object.entries(hvbPlaceGroups)) {
    if (places.some((p) => p.toLowerCase() === lowerPlace)) {
      return groupName;
    }
  }
  return place;
}

// Get grouped display name for HVB withWhom (case insensitive)
export function getHvbGroupForWithWhom(withWhom: string): string {
  if (!withWhom) return 'Okänt';

  const lowerWithWhom = withWhom.toLowerCase();
  for (const [groupName, people] of Object.entries(hvbWithWhomGroups)) {
    if (people.some((p) => p.toLowerCase() === lowerWithWhom)) {
      return groupName;
    }
  }
  return withWhom;
}

// Get all categories in a group
export function getCategoriesInGroup(groupName: string): string[] {
  return hvbCategoryGroups[groupName] || [];
}

// Define category sets
const categorySets: Record<CategorySetType, CategorySet> = {
  default: {
    activities: activitiesDefault,
    feelings: feelingDefault,
    places: placesDefault,
    withWhom: withWhomDefault,
  },
  hvb: {
    activities: activitiesUnga,
    feelings: feelingHvb,
    places: placesBoende,
    withWhom: withWhomBoende,
  },
};

// Get categories based on the selected set
export const getCategorySet = (categorySetType: CategorySetType): CategorySet => {
  return categorySets[categorySetType];
};

// Get specific category type
export const getActivities = (categorySetType: CategorySetType) => {
  return categorySets[categorySetType].activities;
};

export const getFeelings = (categorySetType: CategorySetType) => {
  return categorySets[categorySetType].feelings;
};

export const getPlaces = (categorySetType: CategorySetType) => {
  return categorySets[categorySetType].places;
};

export const getWithWhom = (categorySetType: CategorySetType) => {
  return categorySets[categorySetType].withWhom;
};

// Translation functions for categories
export function translateCategory(category: string, categorySetType: CategorySetType): string {
  if (categorySetType === 'default') {
    return translateCategoryDefault(category);
  } else {
    return translateCategoryHvb(category);
  }
}

export function translatePlace(place: string, categorySetType: CategorySetType): string {
  if (categorySetType === 'default') {
    return translatePlaceDefault(place);
  } else {
    return translatePlaceHvb(place);
  }
}

export function translateWithWhom(withWhom: string, categorySetType: CategorySetType): string {
  if (categorySetType === 'default') {
    return translateWithWhomDefault(withWhom);
  } else {
    return translateWithWhomHvb(withWhom);
  }
}

export function translateFeeling(feeling: string, categorySetType: CategorySetType): string {
  if (categorySetType === 'default') {
    return translateFeelingDefault(feeling);
  } else {
    return translateFeelingHvb(feeling);
  }
}

// Default category translations
function translateCategoryDefault(category: string): string {
  const translations: Record<string, string> = {
    'Egenomsorg': 'Self care',
    'Omvårdnad': 'Nursing care',
    'Hemvård': 'Home care',
    'Avkoppling': 'Relaxation',
    'Förflyttning': 'Movement',
    'Fixa mat': 'Fix food',
    'Arbete/skola': 'Work/school',
  };
  return translations[category] || category;
}

function translatePlaceDefault(place: string): string {
  const translations: Record<string, string> = {
    'Hemma inomhus': 'Home inside',
    'Hemma utomhus': 'Home outside',
    'Arbete': 'Work',
    'Skola': 'School',
    'Annat': 'Other',
  };
  return translations[place] || place;
}

function translateWithWhomDefault(withWhom: string): string {
  const translations: Record<string, string> = {
    'Jag': 'Myself',
    'Med familj': 'With family',
    'Med andra': 'With others',
  };
  return translations[withWhom] || withWhom;
}

function translateFeelingDefault(feeling: string): string {
  const translations: Record<string, string> = {
    'Neutral': 'Neutral',
    'Glad': 'Happy',
    'Koncentrerad': 'Concentrated',
    'Nedstämd': 'Depressed',
    'Pigg': 'Alert',
    'Stressad': 'Stressed',
    'Arg': 'Angry',
    'Trött': 'Tired',
    'Sömnig': 'Sleepy',
  };
  return translations[feeling] || feeling;
}

// HVB category translations
function translateCategoryHvb(category: string): string {
  const translations: Record<string, string> = {
    'Egenomsorg': 'Self care',
    'Omvårdnad': 'Care for others',
    'Hemvård': 'Home care',
    'Avkoppling': 'Relaxation',
    'Umgänge': 'Socializing',
    'Skola': 'School',
    'Kreativitet': 'Creativity',
    'Annat': 'Other',
  };
  return translations[category] || category;
}

function translatePlaceHvb(place: string): string {
  // For HVB, you might want to translate more specifically
  // For now, returning the place as-is
  return place;
}

function translateWithWhomHvb(withWhom: string): string {
  const translations: Record<string, string> = {
    'Själv': 'Alone',
    'Andra ungdom/ar på HVB': 'Other youth at HVB',
    'Kompis/ar utanför HVB': 'Friends outside HVB',
    'Personal': 'Staff',
    'Personal och ungdomar': 'Staff and youth',
    'Skolpersonal': 'School staff',
    'Vårdnadshavare': 'Guardian',
    'God man': 'Trustee',
    'Socialtjänst': 'Social services',
    'Hälso- och sjukvård': 'Healthcare',
    'Psykiatri': 'Psychiatry',
    'Rättsvårdande': 'Legal',
    'Flera aktörer': 'Multiple actors',
    'Annan myndighet': 'Other authority',
    'Annan': 'Other',
  };
  return translations[withWhom] || withWhom;
}

function translateFeelingHvb(feeling: string): string {
  const translations: Record<string, string> = {
    'Glad': 'Happy',
    'Pigg': 'Alert',
    'Trygg': 'Secure',
    'Lugn': 'Calm',
    'Orolig': 'Worried',
    'Trött': 'Tired',
    'Omotiverad': 'Unmotivated',
    'Stressad': 'Stressed',
    'Ledsen': 'Sad',
    'Rädd': 'Scared',
    'Arg': 'Angry',
    'Neutral (t ex vid sömn)': 'Neutral (e.g. during sleep)',
    'Annat': 'Other',
  };
  return translations[feeling] || feeling;
}
